import PatientList from '@/components/data/patient/PatientList';
import useCoordinatingGroups from '@/hooks/data/useCoordinatingGroups';
import useCountries from '@/hooks/data/useCountries';
import useInstitutions from '@/hooks/data/useInstitutions';
import useMasterGroups from '@/hooks/data/useMasterGroups';
import usePatients from '@/hooks/data/usePatients';
import { Grid, Paper, styled, Typography, useTheme } from '@mui/material';
import { FC, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import TrialContext from '../contexts/data/TrialContext';
import { Form, FormErrorDisplayMode, IFormLabel, InputOnlyField, Select, Text, ILookup, IValidationError, Suggestion, Autocomplete, asyncDebounce } from '@ngt/forms'
import { SourceDataVerificationPermission, ValidationErrorType, ValidationResultType } from '@/api/dtos';
import { ValidateOn, SubmitOn, IFormSubmit, IFormState } from '@ngt/forms-core';
import PageTitle, { TITLE_SEPARATOR } from '@/components/layouts/PageTitle';
import usePermissions from '@/hooks/utility/usePermissions';
import { faDna, faUsers } from '@fortawesome/duotone-light-svg-icons';
import Breadcrumbs from '@/components/layouts/Breadcrumbs';

const labels: Array<Partial<IFormLabel>> = [
    {
        name: 'masterGroup',
        label: 'Master Group'
    },
    {
        name: 'coordinatingGroup',
        label: 'Coordinating Group'
    },
    {
        name: 'country',
        label: 'Country'
    },
    {
        name: 'institution',
        label: 'Institution'
    },
    {
        name: 'patient',
        label: 'Study Number'
    }
]


interface IForm {
    masterGroup?: number;
    coordinatingGroup?: number;
    country?: number;
    institution?: number;
    patient?: number;
}

const initialState: IForm = {
    masterGroup: undefined,
    coordinatingGroup: undefined,
    country: undefined,
    institution: undefined,
    patient: undefined
}

const StickySearch = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    position: 'sticky',
    top: 48 /* header height */ + 24,
    
    '@media (min-width: 600px)': {
        top: 64 /* header height */ + 24,
    }
}));

const GridSearchColumn = styled(Grid)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        order: 2
    }
}));

const coordinatingGroupFilterFunction = (formState: IFormState<IForm, IValidationError>, propertyPath: string, lookup: Partial<ILookup> | undefined): Partial<ILookup> | undefined => {
    return {
        ...lookup,
        items: lookup?.items?.filter(x => !formState.value?.masterGroup || (x as any).original.masterGroupId === formState.value?.masterGroup)
    }
}

const institutionFilterFunction = (formState: IFormState<IForm, IValidationError>, propertyPath: string, lookup: Partial<ILookup> | undefined): Partial<ILookup> | undefined => {
    return {
        ...lookup,
        items: lookup?.items?.filter(x => 
            (!formState.value?.masterGroup || (x as any).original.masterGroupId === formState.value?.masterGroup) && 
            (!formState.value?.coordinatingGroup || (x as any).original.coordinatingGroupId === formState.value?.coordinatingGroup) && 
            (!formState.value?.country || (x as any).original.countryId === formState.value?.country))
    }
}

const Patients: FC = () => {

    const { trialCode } = useParams();

    const initialValue = useMemo(() => {
        return {...initialState};
    }, [trialCode])

    const { data: trial } = useContext(TrialContext);
    const { data: patients, loading: patientsLoading, validating: patientsUpdating } = usePatients(trialCode!, { revalidateOnFocus: false });
    const { data: institutions, loading: institutionsLoading, validating: institutionsUpdating  } = useInstitutions(trialCode!, { revalidateOnFocus: false });
    const { data: countries, loading: countriesLoading, validating: countriesUpdating  } = useCountries(trialCode!, { revalidateOnFocus: false });
    const { data: coordinatingGroups, loading: coordinatingGroupsLoading, validating: coordinatingGroupsUpdating  } = useCoordinatingGroups(trialCode!, { revalidateOnFocus: false });
    const { data: masterGroups, loading: masterGroupsLoading, validating: masterGroupsUpdating  } = useMasterGroups(trialCode!, { revalidateOnFocus: false });



    const loadingData = patientsLoading ||  institutionsLoading || countriesLoading || coordinatingGroupsLoading || masterGroupsLoading;
    const updating = !loadingData && (patientsUpdating || institutionsUpdating || countriesUpdating || coordinatingGroupsUpdating || masterGroupsUpdating);

    const { 
        data: [
            hasViewPatient, 
            hasViewMasterGroup, 
            hasViewCoordinatingGroup, 
            hasViewCountry, 
            hasViewInstitution
        ] = [], 
        loading: permissionsLoading, 
        validating: permissionsValidating
    } = usePermissions(
        [
            SourceDataVerificationPermission.ViewPatient, 
            SourceDataVerificationPermission.ViewMasterGroup, 
            SourceDataVerificationPermission.ViewCoordinatingGroup, 
            SourceDataVerificationPermission.ViewCountry, 
            SourceDataVerificationPermission.ViewInstitution
        ], 
        loadingData,
        trial?.id,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        {
            revalidateOnFocus: false
        }
    );

    const loading = loadingData || permissionsLoading || permissionsValidating;
    
    const [formValue, setFormValue] = useState(initialValue);


    
    const breadcrumbs = useMemo(() => {
        return [
            {
                label: 'Trials',
                link: `/`,
                icon: faDna
            },
            {
                label: 'Patients',
                link: `/${trialCode}/participants`,
                icon: faUsers
            }
        ];
    }, []);


    const theme = useTheme();
    useEffect(() => {
        setFormValue({...initialState})
    }, [trialCode])

    const onSubmit: IFormSubmit<IForm, IValidationError> = useCallback(async ( state: any ) => {
        setFormValue(state.value!);

        return {}
    }, [setFormValue])



    const lookups = useMemo(() => {
        const output: ILookup[] = [
            {
                propertyName: 'masterGroup',
                items: masterGroups?.map((x, i) => ({
                    id: x.id as any,
                    value: x.name as any,
                    order: i,
                    original: x
                })) ?? []
            },
            {
                propertyName: 'coordinatingGroup',
                items: coordinatingGroups?.map((x, i) => ({
                    id: x.id as any,
                    value: x.name as any,
                    order: i,
                    original: x
                })) ?? []
            },
            {
                propertyName: 'country',
                items: countries?.map((x, i) => ({
                    id: x.id as any,
                    value: x.name as any,
                    order: i,
                    original: x
                })) ?? []
            },
            {
                propertyName: 'institution',
                items: institutions?.map((x, i) => ({
                    id: x.id as any,
                    value: x.name as any,
                    order: i,
                    original: x
                })) ?? []
            },
            {
                propertyName: 'patient',
                items: patients?.map((x, i) => ({
                    id: x.id as any,
                    value: x.studyNumber as any,
                    order: i,
                    original: x
                })) ?? []
            }
        ];

        return output;
    }, [institutions, countries, coordinatingGroups, masterGroups, patients]);

    const patientFilterFunction = useCallback((formState: IFormState<IForm, IValidationError>, propertyPath: string, lookup: Partial<ILookup> | undefined): Partial<ILookup> | undefined => {
        
        
        return {
            ...lookup,
            items: lookup?.items?.filter(x => {
                if (!!formState.value?.institution && (x as any).original.institutionId == formState.value?.institution) {
                    return true;
                }

                const inst = institutions?.find(y => y.id == (x as any).original.institutionId);
                
                return (!formState.value?.masterGroup || inst?.masterGroupId === formState.value?.masterGroup) && 
                    (!formState.value?.coordinatingGroup || inst?.coordinatingGroupId === formState.value?.coordinatingGroup) && 
                    (!formState.value?.country || inst?.countryId === formState.value?.country) &&
                    (!formState.value?.institution || inst?.id === formState.value?.institution)
                
            })
        }
    }, [institutions]);

    if (!hasViewPatient && !permissionsLoading && !permissionsValidating) {
        return (
            <Navigate to='/error/403' />
        )
    }



    return (
        <>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <PageTitle  title={`Participants${TITLE_SEPARATOR}${trial?.name}`} />
            <Typography color={theme.palette.secondary.main} variant="h1" gutterBottom>
                Participants
            </Typography>
            <Grid 
                container
                spacing={2}
            >
                <GridSearchColumn 
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={4}
                    xl={3}
                >
                    <StickySearch>
                        <Form
                            initialValue={initialValue}
                            labels={labels}
                            lookups={lookups}
                            debug={import.meta.env.DEV}
                            submitOn={SubmitOn.onChange}
                            onSubmit={onSubmit}
                        >
                            {
                                hasViewMasterGroup && (
                                    <InputOnlyField 
                                        propertyPath="masterGroup"
                                        component={Select}
                                        FormControlProps={{
                                            margin: 'normal'
                                        }}
                                    />
                                )
                            }
                            {
                                hasViewCoordinatingGroup && (
                                    <InputOnlyField 
                                        propertyPath="coordinatingGroup"
                                        component={Select}
                                        FormControlProps={{
                                            margin: 'normal'
                                        }}
                                        LookupFilterProps={{
                                            filterFunction: coordinatingGroupFilterFunction,
                                            subscription: { value: true }
                                        }}
                                    />
                                )
                            }
                            {
                                hasViewCountry && (
                                    <InputOnlyField 
                                        propertyPath="country"
                                        component={Select}
                                        FormControlProps={{
                                            margin: 'normal'
                                        }}
                                    />
                                )
                            }
                            {
                                hasViewInstitution && (
                                    <InputOnlyField 
                                        propertyPath="institution"
                                        component={Select}
                                        FormControlProps={{
                                            margin: 'normal'
                                        }}
                                        LookupFilterProps={{
                                            filterFunction: institutionFilterFunction,
                                            subscription: { value: true }
                                        }}
                                    />
                                )
                            }
                            <InputOnlyField 
                                propertyPath="patient"
                                component={Autocomplete}
                                LookupFilterProps={{
                                    filterFunction: patientFilterFunction,
                                    subscription: { value: true }
                                }}
                                TextFieldProps={{
                                    margin: 'normal'
                                }}
                                openOnFocus
                            />
                        </Form>
                    </StickySearch>
                </GridSearchColumn>
                <Grid 
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={8}
                    xl={9}
                >
                    <Paper>
                        <PatientList
                            institutions={institutions}
                            patients={patients}
                            trial={trial}
                            updating={updating}
                            loading={loading}
                            selectedMasterGroupId={formValue?.masterGroup}
                            selectedCoordinatingGroupId={formValue?.coordinatingGroup}
                            selectedCountryId={formValue?.country}
                            selectedInstitutionId={formValue?.institution}
                            selectedPatientId={formValue?.patient}
                        />
                    </Paper>
                </Grid>
            </Grid>
        </>
    );
}

export default Patients;